import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { Play, Clock, X } from "lucide-react";

export default function VideoLibraryPage() {
  const [videos, setVideos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("all");
  const [activeVideo, setActiveVideo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/videos");
      setVideos(data.videos);
      setCategories(data.categories);
      setLoading(false);
    })();
  }, []);

  const filtered = useMemo(() => (category === "all" ? videos : videos.filter((v) => v.category === category)), [videos, category]);

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto px-6 py-10" data-testid="video-library-page">
        <div className="overline mb-2">Video Library</div>
        <h1 className="font-serif text-4xl md:text-5xl text-[var(--dw-navy)] mb-3 tracking-tight leading-tight">
          28 lectures. All the essentials.
        </h1>
        <p className="text-neutral-600 max-w-2xl mb-8">
          Curated from the acclaimed Affinity Real Estate & Mortgage Training playlist —
          RESPA, TILA, ARMs, math, ethics, and everything in between.
        </p>

        {/* Category filter */}
        <div className="flex flex-wrap gap-2 mb-8">
          {[["all", "All"]].concat(categories.map((c) => [c, c])).map(([v, lbl]) => (
            <button
              key={v}
              onClick={() => setCategory(v)}
              data-testid={`video-cat-${v}`}
              className={`px-3 py-1.5 text-xs rounded-sm border transition-colors ${
                category === v ? "border-[var(--dw-navy)] bg-[var(--dw-navy)] text-[var(--dw-parchment)]" : "border-[var(--dw-navy)]/15 text-neutral-600 hover:border-[var(--dw-navy)]/40"
              }`}
            >
              {lbl}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid md:grid-cols-3 gap-4">{[1, 2, 3, 4, 5, 6].map((i) => <div key={i} className="dw-card h-56 animate-pulse" />)}</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((v) => (
              <button
                key={v.id}
                onClick={() => setActiveVideo(v)}
                data-testid={`video-card-${v.id}`}
                className="dw-card text-left group overflow-hidden hover:border-[var(--dw-gold)]"
              >
                <div className="relative aspect-video bg-neutral-900 overflow-hidden">
                  <img
                    src={`https://i.ytimg.com/vi/${v.id}/hqdefault.jpg`}
                    alt={v.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/10 transition-colors">
                    <div className="w-12 h-12 bg-white/95 rounded-full flex items-center justify-center">
                      <Play className="w-5 h-5 text-[var(--dw-navy)] ml-0.5" strokeWidth={2} fill="currentColor" />
                    </div>
                  </div>
                  <div className="absolute bottom-2 right-2 bg-black/80 text-white text-[10px] px-1.5 py-0.5 rounded-sm font-mono-plex flex items-center gap-1">
                    <Clock className="w-3 h-3" strokeWidth={1.5} /> {v.duration}
                  </div>
                </div>
                <div className="p-4">
                  <div className="overline mb-1 text-neutral-500">Module {v.module} · {v.category}</div>
                  <div className="font-serif text-base text-[var(--dw-navy)] leading-tight line-clamp-2">{v.title}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {activeVideo && (
        <div
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setActiveVideo(null)}
        >
          <button
            onClick={() => setActiveVideo(null)}
            data-testid="close-video"
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2"
          >
            <X className="w-6 h-6" strokeWidth={1.5} />
          </button>
          <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="aspect-video bg-black rounded-sm overflow-hidden shadow-2xl">
              <iframe
                className="w-full h-full"
                src={`https://www.youtube.com/embed/${activeVideo.id}?autoplay=1`}
                title={activeVideo.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <div className="mt-4 text-white">
              <div className="overline text-[var(--dw-gold)] mb-1">Module {activeVideo.module} · {activeVideo.category}</div>
              <div className="font-serif text-xl">{activeVideo.title}</div>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
